import { API, graphqlOperation } from 'aws-amplify';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../../graphql/iot-documents';
import {
  createReconnectingSubscription,
  classifyEquipmentType,
} from '../../../utils';
import { store } from '../..';

// Throttle leading-edge por thing. Subscriptions onUpdateIOT podem
// disparar varias vezes por segundo (especialmente em modo TV onde
// 100+ things estao ativas). Cada dispatch re-renderiza qualquer
// componente que useSelector da slice dashboard.realtimeData.
// Limitamos a 1 dispatch por thing por 1000ms — visualmente nao se
// percebe diferenca pra valores que mudam suavemente (potencia, vazao)
// mas reduz custo de render em ate ~10x.
const REALTIME_THROTTLE_MS = 1000;
const lastDispatchByThing = new Map();

const abreConexao = async (thing) => {
  const updateIotSubscription = await onUpdateIOTDocument();
  // Wrappa em createReconnectingSubscription (backoff exponencial
  // 1s -> 2s -> 4s ... 30s max). Antes era subscribe direto sem
  // reconnect: ao primeiro erro de rede / glitch do AppSync, a
  // subscription morria pra sempre e o flag 'Reconectando' nunca
  // saia. Agora reconecta sozinha; o flag volta pra 'ok' assim que
  // a primeira mensagem nova chegar.
  const recon = createReconnectingSubscription({
    label: `onUpdateIOT/${thing.thing}`,
    buildSubscription: ({ next, error }) =>
      API.graphql(
        graphqlOperation(updateIotSubscription, { thingName: thing.thing })
      ).subscribe({ next, error }),
    onNext: (subscribeData) => {
      // Recebemos update — subscription saudavel. Dispatch so se
      // estava em estado 'failed' antes (evita spam de actions).
      const healthState = store.getState().primeiroNivel?.subscriptionHealth;
      if (healthState && healthState !== 'ok') {
        store.dispatch({
          type: 'PRIMEIRO/SET_SUBSCRIPTION_HEALTH',
          health: 'ok',
        });
      }

      if (
        subscribeData.value &&
        subscribeData.value.data &&
        subscribeData.value.data.onUpdateIOT
      ) {
        // Throttle: skipa update se outro foi feito ha menos de 1s
        // pra mesma thing. Leading-edge (deixa o primeiro passar). O
        // estado nao 'congela' porque a proxima atualizacao apos 1s
        // dispara normal.
        const now = Date.now();
        const prev = lastDispatchByThing.get(thing.thing) || 0;
        if (now - prev < REALTIME_THROTTLE_MS) return;
        lastDispatchByThing.set(thing.thing, now);

        const total = {
          onTime: subscribeData.value.data.onUpdateIOT.PotenciaAtivaMedia,
          potencia: subscribeData.value.data.onUpdateIOT.PotenciaAtivaMedia,
          vazao: subscribeData.value.data.onUpdateIOT.Vazao,
        };
        store.dispatch({
          type: 'DASHBOARD/SET_REALTIME_DATA',
          data: total,
        });
        // Tracking per-thing pra freshness chip nos cards do /cliente.
        // Ts em ms (Date.now). Stale-threshold no consumer (chip pulsa
        // verde se < 60s, cinza se mais velho).
        store.dispatch({
          type: 'PRIMEIRO/SET_THING_LAST_SEEN',
          thingName: thing.thing,
          ts: Date.now(),
        });
      }
    },
    onError: () => {
      // Subscription quebrou — sinaliza saude 'failed'. O helper ja
      // esta agendando o retry com backoff exponencial; assim que
      // a primeira mensagem chegar o onNext acima vira 'ok'.
      store.dispatch({
        type: 'PRIMEIRO/SET_SUBSCRIPTION_HEALTH',
        health: 'failed',
      });
    },
  });
  return recon;
};

export async function conectaAplicacoes({ parques }) {
  const conexoes = [];
  const conexoesAbertas = store.getState().primeiroNivel.conexoes;
  conexoesAbertas.forEach((ƒ) => ƒ());

  // Reset do health flag — comeca otimista. Se alguma subscription
  // falhar, o onError vai marcar 'failed' de novo. Sem isso, ao
  // navegar entre /dashboard e /cliente o flag de uma sessao anterior
  // (que tinha falhado) persistia mostrando "Reconectando..."
  // permanentemente.
  if (store.getState().primeiroNivel?.subscriptionHealth !== 'ok') {
    store.dispatch({
      type: 'PRIMEIRO/SET_SUBSCRIPTION_HEALTH',
      health: 'ok',
    });
  }

  // Antes achatava todas as things num array e perdia o link com o parque.
  // Agora itera por parque mantendo a associacao thing -> parqueId, o que
  // permite calcular um snapshot per-parque (usado no tooltip do marker
  // no mapa). Os totais globais (uchas/ebas) continuam sendo computados.
  const total = { onTimeUcha: 0, vazaoUcha: 0, potenciaEba: 0, vazaoEba: 0 };
  const parqueRealtime = {};
  const iotQuery = await getIOTDocument();

  await Promise.all(
    parques.map(async (parque) => {
      const parqueAggregate = {
        onTimeUcha: 0,
        vazaoUcha: 0,
        potenciaEba: 0,
        vazaoEba: 0,
      };

      const parqueThings = (parque.appliance || [])
        .map(({ thing }) => thing || [])
        .flat();

      await Promise.all(
        parqueThings.map(async (thing) => {
          try {
            const resp = await API.graphql(
              graphqlOperation(iotQuery, { thingName: thing.thing })
            );
            const data =
              'getIOT' in resp ? resp.getIOT || {} : resp.data.getIOT;
            const conexao = await abreConexao(thing);
            conexoes.push(() => conexao.unsubscribe());

            const onTime = Number(data?.PotenciaAtivaMedia) || 0;
            const vazao = Number(data?.Vazao) || 0;
            // classifyEquipmentType normaliza 'tga'/'ucha' -> 'ucha' e
            // 'bomba'/'eba' -> 'eba' (case-insensitive). Antes so casava
            // '=== tga'/'=== bomba' e descartava o resto, sumindo maquinas
            // do somatorio (ver util). thing.type pode ser objeto ou string.
            const categoria = classifyEquipmentType(thing.type);

            if (categoria === 'ucha') {
              parqueAggregate.onTimeUcha += onTime;
              parqueAggregate.vazaoUcha += vazao;
              total.onTimeUcha += onTime;
              total.vazaoUcha += vazao;
            } else if (categoria === 'eba') {
              parqueAggregate.potenciaEba += onTime; // PotenciaAtivaMedia
              parqueAggregate.vazaoEba += vazao;
              total.potenciaEba += onTime;
              total.vazaoEba += vazao;
            }
          } catch (_) {
            // thing com erro AppSync e descartada silenciosamente
          }
        })
      );

      parqueRealtime[parque.id] = parqueAggregate;
    })
  );

  store.dispatch({
    type: 'PRIMEIRO/SET_VALORES_UCHAS_EBAS',
    uchas: { onTime: total.onTimeUcha, vazao: total.vazaoUcha },
    ebas: { potencia: total.potenciaEba, vazao: total.vazaoEba },
    conexoes,
  });

  store.dispatch({
    type: 'PRIMEIRO/SET_PARQUE_REALTIME',
    parqueRealtime,
  });
}
